import { initContract } from "@ts-rest/core";
import { z } from "zod";
import {
    PersistedViewSchema,
    ViewScopeSchema,
    ViewType,
} from "@common/types/views/views";
import { ErrorMessageSchema } from "@common/schemas/error.schema";

const c = initContract();

const TimestampedViewSchema = PersistedViewSchema.extend({
    createdAt: z.date(),
    updatedAt: z.date(),
});

const CreateViewParamsSchema = PersistedViewSchema.omit({
    id: true,
});

const ScopedCreateViewSchema = CreateViewParamsSchema.and(ViewScopeSchema);

const PartialViewSchema = PersistedViewSchema.omit({
    id: true,
}).partial();

type ViewOutput = z.output<typeof TimestampedViewSchema>;
type ViewInput = z.input<typeof TimestampedViewSchema>;
type CreateViewOutput = z.output<typeof ScopedCreateViewSchema>;
type CreateViewInput = z.input<typeof ScopedCreateViewSchema>;
type UpdateViewOutput = z.output<typeof PartialViewSchema>;
type UpdateViewInput = z.input<typeof PartialViewSchema>;

// Typed through the aliases above so the contract's declaration names these
// types instead of inlining every view's filters shape per route, which
// overflows TS7056's serialization limit.
const ViewSchema: z.ZodType<ViewOutput, z.ZodTypeDef, ViewInput> =
    TimestampedViewSchema;
const CreateViewSchema: z.ZodType<
    CreateViewOutput,
    z.ZodTypeDef,
    CreateViewInput
> = ScopedCreateViewSchema;
const UpdateViewSchema: z.ZodType<
    UpdateViewOutput,
    z.ZodTypeDef,
    UpdateViewInput
> = PartialViewSchema;

const GetViewsQuerySchema = z
    .object({
        type: z.nativeEnum(ViewType).optional(),
    })
    .and(ViewScopeSchema);

const ViewParamsSchema = z.object({
    id: z.string().uuid(),
});

const VIEW_PATH_PREFIX = { pathPrefix: "/view" } as const;

const viewCoreContract = c.router(
    {
        getViews: {
            method: "GET",
            path: "/",
            query: GetViewsQuerySchema,
            responses: {
                200: z.object({ views: z.array(ViewSchema) }),
                404: ErrorMessageSchema,
            },
        },
        createView: {
            method: "POST",
            path: "/",
            body: CreateViewSchema,
            responses: {
                201: z.object({ view: ViewSchema }),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        deleteView: {
            method: "DELETE",
            path: "/:id",
            pathParams: ViewParamsSchema,
            responses: {
                204: z.void(),
                404: ErrorMessageSchema,
            },
        },
    },
    VIEW_PATH_PREFIX,
);

const viewUpdateContract = c.router(
    {
        updateView: {
            method: "PATCH",
            path: "/:id",
            pathParams: ViewParamsSchema,
            body: UpdateViewSchema,
            responses: {
                200: z.object({ view: ViewSchema }),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    VIEW_PATH_PREFIX,
);

export type ViewContract = typeof viewCoreContract & typeof viewUpdateContract;

export const viewContract: ViewContract = {
    ...viewCoreContract,
    ...viewUpdateContract,
};

export type CreateView = z.infer<typeof CreateViewSchema>;
export type CreateViewParams = z.infer<typeof CreateViewParamsSchema>;
export type UpdateView = z.infer<typeof UpdateViewSchema>;
export type GetViewsQuery = z.infer<typeof GetViewsQuerySchema>;
export type ViewParams = z.infer<typeof ViewParamsSchema>;
